import { type RefObject, useEffect, useRef } from 'react'

/** Where focus starts when an overlay opens: an element, or a function that finds one in the panel. */
export type OverlayInitialFocus =
  | RefObject<HTMLElement>
  | ((panel: HTMLElement) => HTMLElement | null | undefined)

export interface OverlayOptions {
  /** Whether the overlay is showing. */
  open: boolean
  /** Escape asks the overlay to close. Not called while `busy`. */
  onClose: () => void
  /** A request is running: Escape does nothing. */
  busy?: boolean
  /** Where focus starts. Default: the first element Tab can reach, else the panel itself. */
  initialFocus?: OverlayInitialFocus
}

const TABBABLE = [
  'a[href]',
  'area[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'iframe',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',')

/** The elements inside `container` that Tab can reach, in order. */
export function tabbableElements(container: HTMLElement | null | undefined): HTMLElement[] {
  if (!container) return []
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) =>
      !el.matches(':disabled') &&
      Number(el.getAttribute('tabindex') ?? 0) >= 0 &&
      !el.closest('[inert], [hidden]') &&
      // Browsers can tell whether it's rendered (not display: none); jsdom can't.
      (typeof el.checkVisibility !== 'function' ||
        el.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true })),
  )
}

// Open overlays, oldest first. Only the last one (on top) answers Escape and
// keeps Tab inside, so a confirm opened from a drawer closes on its own.
const openOverlays: RefObject<HTMLElement>[] = []

// Nested overlays share one lock: the first to open locks the page's scroll,
// the last to close gives it back.
let scrollLocks = 0
let pageOverflow = ''

function focusFirst(candidates: (HTMLElement | null | undefined)[]) {
  for (const el of candidates) {
    if (!el) continue
    el.focus({ preventScroll: true })
    if (document.activeElement === el) return
  }
}

function keepTabInside(e: KeyboardEvent, panel: HTMLElement) {
  const items = tabbableElements(panel)
  if (items.length === 0) {
    e.preventDefault()
    panel.focus({ preventScroll: true })
    return
  }
  const first = items[0]
  const last = items[items.length - 1]
  const active = document.activeElement
  const inside = active instanceof Node && panel.contains(active)
  if (e.shiftKey && (!inside || active === first || active === panel)) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && (!inside || active === last)) {
    e.preventDefault()
    first.focus()
  }
}

/**
 * What every modal overlay (dialog, drawer, confirm) needs, in one place. Put
 * the returned ref on the panel and give the panel `tabIndex={-1}`.
 *
 * While `open`:
 * - Escape calls `onClose`, unless `busy`;
 * - focus moves into the panel (`initialFocus`, else the first element Tab can
 *   reach), Tab and Shift+Tab wrap inside it, and when it closes focus goes back
 *   to whatever had it before (usually the button that opened it);
 * - the page behind doesn't scroll.
 *
 * Overlays opened on top of each other stack: only the top one reacts to keys,
 * and not while focus is in another dialog, like the command palette.
 */
export function useOverlay<T extends HTMLElement = HTMLDivElement>({
  open,
  onClose,
  busy = false,
  initialFocus,
}: OverlayOptions): RefObject<T> {
  const panelRef = useRef<T>(null)
  // The keyboard handler reads the latest props without reopening the overlay.
  const latest = useRef({ onClose, busy })
  latest.current = { onClose, busy }
  const initialFocusRef = useRef(initialFocus)
  initialFocusRef.current = initialFocus

  useEffect(() => {
    if (!open) return
    const entry: RefObject<HTMLElement> = panelRef
    openOverlays.push(entry)

    if (scrollLocks++ === 0) {
      pageOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }

    // Remember the opener, then move focus in (unless something inside, like an
    // autoFocus field, already has it).
    const panel = panelRef.current
    const active = document.activeElement
    const opener =
      active instanceof HTMLElement && active !== document.body && !panel?.contains(active) ? active : null
    if (panel && !panel.contains(active)) {
      const wanted = initialFocusRef.current
      focusFirst([
        typeof wanted === 'function' ? wanted(panel) : wanted?.current,
        tabbableElements(panel)[0],
        panel,
      ])
    }

    const onKeyDown = (e: KeyboardEvent) => {
      const current = panelRef.current
      if (openOverlays[openOverlays.length - 1] !== entry || e.defaultPrevented || !current) return
      // Keys in another dialog on top (the command palette, say) are that dialog's.
      const focused = document.activeElement
      if (focused && !current.contains(focused) && focused.closest('[role="dialog"], [role="alertdialog"]')) return
      if (e.key === 'Escape' && !e.isComposing) {
        if (latest.current.busy) return
        e.preventDefault()
        latest.current.onClose()
      } else if (e.key === 'Tab') {
        keepTabInside(e, current)
      }
    }
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      const index = openOverlays.indexOf(entry)
      if (index >= 0) openOverlays.splice(index, 1)
      if (--scrollLocks === 0) document.body.style.overflow = pageOverflow
      // Back to the opener; if it's gone, to the overlay underneath.
      const below = openOverlays[openOverlays.length - 1]?.current
      const target = opener?.isConnected ? opener : below?.isConnected ? below : null
      target?.focus({ preventScroll: true })
    }
  }, [open])

  return panelRef
}
